
import { useState } from "react";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShoppingCart, Star, ShoppingBag } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface Product {
  id: number;
  name: string;
  description: string;
  price: string;
  image: string;
  rating: number;
  reviews: number;
  seller: string;
  inStock: boolean;
  category: string;
}

export const MarketplaceProducts = () => {
  const { toast } = useToast();
  const [cart, setCart] = useState<number[]>([]);

  const products: Product[] = [
    {
      id: 1,
      name: "Web3 Development eBook",
      description: "Comprehensive guide to building decentralized applications",
      price: "$49.99",
      image: "https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c",
      rating: 4.8,
      reviews: 124,
      seller: "DGK Publications",
      inStock: true,
      category: "Books"
    },
    {
      id: 2,
      name: "Premium Code Templates Bundle",
      description: "50+ premium UI components and templates for React applications",
      price: "$79.99",
      image: "https://images.unsplash.com/photo-1555066931-4365d14bab8c",
      rating: 4.9,
      reviews: 87,
      seller: "CodeMasters Inc",
      inStock: true,
      category: "Templates"
    },
    {
      id: 3,
      name: "Blockchain Certification Course",
      description: "Become a certified blockchain developer with this comprehensive course",
      price: "$199.99",
      image: "https://images.unsplash.com/photo-1591696205602-2f950c417cb9",
      rating: 4.7,
      reviews: 203,
      seller: "Crypto Academy",
      inStock: true,
      category: "Courses"
    },
    {
      id: 4,
      name: "Smart Contract Security Toolkit",
      description: "Tools and resources for securing your smart contracts",
      price: "$129.99",
      image: "https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5",
      rating: 4.6,
      reviews: 56,
      seller: "SecureChain Labs",
      inStock: false,
      category: "Tools"
    },
    {
      id: 5,
      name: "UI/UX Design System for DApps",
      description: "Complete design system for decentralized applications",
      price: "$89.99",
      image: "https://images.unsplash.com/photo-1561070791-2526d30994b5",
      rating: 4.8,
      reviews: 94,
      seller: "DesignDAO",
      inStock: true,
      category: "Design"
    },
    {
      id: 6,
      name: "Crypto Project Management Templates",
      description: "Project management templates for blockchain startups",
      price: "$39.99",
      image: "https://images.unsplash.com/photo-1434030216411-0b793f4b4173",
      rating: 4.5,
      reviews: 47,
      seller: "BlockManagers",
      inStock: true,
      category: "Templates"
    }
  ];

  const handleAddToCart = (productId: number) => {
    setCart([...cart, productId]);
    const product = products.find(p => p.id === productId);
    toast({
      title: "Added to cart!",
      description: `${product?.name} has been added to your cart.`,
    });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Digital Products</h2>
        <Button variant="outline" className="flex items-center gap-2">
          <ShoppingCart className="w-4 h-4" />
          Cart ({cart.length})
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.map((product) => (
          <Card key={product.id} className="overflow-hidden hover:shadow-lg transition-all">
            <div className="h-48 bg-gray-200 relative">
              <img 
                src={product.image} 
                alt={product.name} 
                className="w-full h-full object-cover"
              />
              {!product.inStock && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <span className="text-white font-semibold text-lg">Out of Stock</span>
                </div>
              )}
            </div>
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold text-lg">{product.name}</h3>
                  <p className="text-sm text-gray-500">{product.seller}</p>
                </div>
                <Badge className="bg-sky-100 text-sky-800">{product.category}</Badge>
              </div>
              <p className="text-gray-600 text-sm mt-2 line-clamp-2">{product.description}</p>
              <div className="flex items-center mt-2">
                <Star className="w-4 h-4 text-yellow-400 fill-current" />
                <span className="ml-1 text-sm">{product.rating}</span>
                <span className="text-sm text-gray-500 ml-1">({product.reviews} reviews)</span>
              </div>
            </CardContent>
            <CardFooter className="flex justify-between items-center p-4 bg-gray-50">
              <div className="font-bold text-lg">{product.price}</div>
              <Button 
                onClick={() => handleAddToCart(product.id)}
                disabled={!product.inStock}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <ShoppingBag className="w-4 h-4 mr-2" />
                {product.inStock ? "Add to Cart" : "Sold Out"}
              </Button>
            </CardFooter>
          </Card>
        ))}
      </div>
    </div>
  );
};
